// frontend/src/utils/fetchWithAuth.ts
/**
 * @fileoverview Unified API Utility
 * Replaces direct axios/fetch calls with consistent authentication and error handling.
 * 
 * @example
 * // Using fetchWithAuth (raw Response)
 * const response = await fetchWithAuth('/api/reports');
 * const data = await response.json();
 * 
 * @example
 * // Using apiClient (axios-like)
 * import { apiClient } from '../utils/fetchWithAuth';
 * const { data } = await apiClient.get('/api/reports');
 * await apiClient.post('/api/submit', { idea: 'test' });
 * 
 * @module fetchWithAuth
 */

import { API_CONFIG } from './config';
import i18n from '../i18n';
import { consumeResponseWithSignal, FetchTimeoutError } from './fetchWithTimeout';
import {
  getFounderSupportSessionHeader,
  readFounderSupportSession,
} from './founderSupportSession';
import {
  clearAppSessionToken,
  getAppSessionCredentials,
  readAppSessionToken,
  readLegacyAppSessionToken,
} from './session';
import { checkSession } from './checkSession';

const BACKEND_URL = API_CONFIG.BACKEND_URL;
const READ_ONLY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const isFounderSupportSessionEndRequest = (url: string, method: string): boolean => {
  if (method !== 'DELETE') return false;
  try {
    const resolved = new URL(url, window.location.origin);
    return /^\/api\/admin\/support-sessions\/[^/]+$/.test(resolved.pathname);
  } catch {
    return false;
  }
};

const blockedFounderSupportResponse = (): Response => new Response(
  JSON.stringify({
    error: 'Founder support sessions are read-only',
    code: 'SUPPORT_SESSION_READ_ONLY',
  }),
  {
    status: 403,
    headers: { 'Content-Type': 'application/json' },
  },
);

// Row 85 SESSION-INVALIDATION-UX-01: tokens to wipe on session expiry. Mirrors
// the auth subset of cacheManager.ts USER_SCOPED_STORAGE_KEYS; kept local to
// avoid importing the heavier cacheManager (which pulls dashboard hooks).
const AUTH_STORAGE_KEYS = ['loggedIn', 'userPlan', 'userId', 'userEmail'];

// Singleton guard: when multiple in-flight requests all 401 simultaneously
// (typical on Node restart / Railway redeploy), trigger the expiry flow once.
let sessionExpiryTriggered = false;
type SessionRevalidationOutcome = 'valid' | 'invalid' | 'unavailable' | 'anonymous';
let sessionRevalidationInFlight: Promise<SessionRevalidationOutcome> | null = null;

const isAuthenticationEndpoint = (url: string): boolean => (
  /\/api\/auth\//i.test(url)
  || /\/api\/loginUser/i.test(url)
  || /\/api\/check-session/i.test(url)
);

const hasSessionHint = (): boolean => {
  try {
    return Boolean(
      readAppSessionToken()
      || readLegacyAppSessionToken()
      || localStorage.getItem('userId')
      || localStorage.getItem('loggedIn') === 'true'
    );
  } catch {
    return false;
  }
};

const revalidateUnauthorizedSession = async (url: string): Promise<SessionRevalidationOutcome> => {
  if (isAuthenticationEndpoint(url) || !hasSessionHint()) return 'anonymous';
  if (sessionRevalidationInFlight) return sessionRevalidationInFlight;

  const validation = checkSession()
    .then((verifiedUser) => verifiedUser ? 'valid' as const : 'invalid' as const)
    .catch(() => {
      // A network/provider outage must not be converted into a logout. The
      // caller still receives the original response and can render a bounded
      // unavailable state while the tab credential remains intact.
      // Unexpected validation failures are also treated as unavailable:
      // only a definitive null session may clear Founder authentication.
      return 'unavailable' as const;
    })
    .finally(() => {
      if (sessionRevalidationInFlight === validation) {
        sessionRevalidationInFlight = null;
      }
    });

  sessionRevalidationInFlight = validation;
  return validation;
};

/**
 * Row 85 SESSION-INVALIDATION-UX-01: handle stale-session 401 from the backend
 * after Node restart (Railway redeploy) wipes the in-memory session cache.
 *
 * Skips auth endpoints (login/signup/logout) to avoid redirect loops on
 * legitimate credential failures.
 */
function handleAuthExpiry(url: string): void {
  if (sessionExpiryTriggered) return;
  // Don't redirect from auth endpoints — a 401 from /api/auth/login is a
  // legitimate "wrong password", not a session expiry.
  if (isAuthenticationEndpoint(url)) return;
  // Only act if the user actually had a session to lose. Anonymous 401s
  // (public pages calling protected endpoints) are not "session expiry".
  const hadSession = hasSessionHint();
  let next = '/get-started';
  try {
    next = window.location.pathname + window.location.search + window.location.hash;
  } catch {
    /* Storage can be unavailable; the current path still remains in the URL. */
  }
  if (!hadSession) return;

  try {
    if (!sessionStorage.getItem('redirectAfterLogin')) {
      sessionStorage.setItem('redirectAfterLogin', next);
    }
  } catch {
    /* The explicit next query still preserves the current route. */
  }

  sessionExpiryTriggered = true;
  clearAppSessionToken();
  try {
    AUTH_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* localStorage may be unavailable in some sandboxes — non-fatal */
  }

  // Show user-facing toast (best-effort dynamic import to avoid bundling cost
  // for users who never hit this path).
  void import('react-hot-toast').then(({ default: toast }) => {
    try {
      toast.error(String(i18n.t('toastSessionExpired', 'Your session expired. Please log in again.')), {
        id: 'session-expired',
        duration: 5000,
      });
    } catch { /* toast failure non-fatal */ }
  }).catch(() => { /* import failure non-fatal */ });

  // Redirect to login. Preserve the path so the user can return after re-auth.
  // setTimeout lets the toast render before navigation.
  setTimeout(() => {
    try {
      // Only redirect if not already on /login (avoid loop) and not on a public page that doesn't need auth.
      if (!/^\/login/.test(window.location.pathname)) {
        window.location.href = `/login?expired=1&next=${encodeURIComponent(next)}`;
      }
    } catch { /* navigation failure non-fatal */ }
  }, 150);
}

/**
 * Base fetch with auth - returns raw Response
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const method = String(options.method || 'GET').toUpperCase();
  if (
    Boolean(readFounderSupportSession())
    && !READ_ONLY_METHODS.has(method)
    && !isFounderSupportSessionEndRequest(url, method)
  ) {
    return blockedFounderSupportResponse();
  }

  const requestWithCurrentSession = async (): Promise<Response> => {
    // Production uses the HttpOnly cookie. Local and preview environments keep
    // the bearer in the current tab only because they cannot receive that
    // cross-site cookie.
    let token: string | null = null;
    try {
      token = readAppSessionToken();
    } catch {
      /* Cookies can still authenticate when local storage is unavailable. */
    }

    // Preserve Headers instances and tuple arrays while layering auth. The
    // support token never replaces the Platform Admin authentication token.
    const headers = new Headers(options.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    Object.entries(getFounderSupportSessionHeader()).forEach(([key, value]) => {
      headers.set(key, value);
    });

    return fetch(url, {
      ...options,
      headers,
      credentials: getAppSessionCredentials(),
    });
  };

  let response = await requestWithCurrentSession();
  if (response.status !== 401 || isAuthenticationEndpoint(url)) return response;

  const revalidation = await revalidateUnauthorizedSession(url);
  if (revalidation === 'valid' && READ_ONLY_METHODS.has(method)) {
    // checkSession may have refreshed the tab bearer. Retrying one idempotent
    // read closes that race without ever replaying a mutation.
    response = await requestWithCurrentSession();
  } else if (revalidation === 'invalid') {
    handleAuthExpiry(url);
  }

  // A second route-specific 401 after a verified session, or an unavailable
  // session service, remains a local access error and never logs out the user.
  return response;
}

/**
 * Authenticated fetch with a bounded wait. An upstream AbortSignal remains an
 * explicit cancellation; only the internal timer is reported as a timeout.
 * Supply a response consumer to keep that lifetime through body decoding.
 */
export function fetchWithAuthTimeout(url: string, options?: RequestInit, timeoutMs?: number): Promise<Response>;
export function fetchWithAuthTimeout<T>(url: string, options: RequestInit, timeoutMs: number, consumeResponse: (response: Response) => Promise<T>): Promise<T>;
export async function fetchWithAuthTimeout<T>(
  url: string,
  options: RequestInit = {},
  timeoutMs = 10_000,
  consumeResponse?: (response: Response) => Promise<T>,
): Promise<Response | T> {
  const controller = new AbortController();
  const upstreamSignal = options.signal;
  let timedOut = false;

  const abortFromUpstream = () => controller.abort();
  if (upstreamSignal?.aborted) {
    controller.abort();
  } else {
    upstreamSignal?.addEventListener('abort', abortFromUpstream, { once: true });
  }

  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetchWithAuth(url, { ...options, signal: controller.signal });
    if (!consumeResponse) return response;
    const result = await consumeResponseWithSignal(response, consumeResponse, controller.signal);
    if (timedOut) throw new FetchTimeoutError(timeoutMs);
    if (upstreamSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return result;
  } catch (error) {
    if (timedOut) throw new FetchTimeoutError(timeoutMs);
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    upstreamSignal?.removeEventListener('abort', abortFromUpstream);
  }
}

/**
 * API client - axios-like interface using fetch
 * Use this instead of importing axios directly
 */
export const apiClient = {
  /**
   * GET request with automatic JSON parsing
   */
  async get<T = unknown>(endpoint: string, config?: { params?: Record<string, string> }): Promise<{ data: T; status: number }> {
    let url = endpoint.startsWith('http') ? endpoint : `${BACKEND_URL}${endpoint}`;
    
    if (config?.params) {
      const searchParams = new URLSearchParams(config.params);
      url += `?${searchParams.toString()}`;
    }
    
    const response = await fetchWithAuth(url);
    const data = await response.json();
    return { data, status: response.status };
  },

  /**
   * POST request with automatic JSON serialization
   */
  async post<T = unknown>(endpoint: string, body?: unknown): Promise<{ data: T; status: number }> {
    const url = endpoint.startsWith('http') ? endpoint : `${BACKEND_URL}${endpoint}`;
    
    const response = await fetchWithAuth(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    
    const data = await response.json();
    return { data, status: response.status };
  },

  /**
   * PUT request with automatic JSON serialization
   */
  async put<T = unknown>(endpoint: string, body?: unknown): Promise<{ data: T; status: number }> {
    const url = endpoint.startsWith('http') ? endpoint : `${BACKEND_URL}${endpoint}`;
    
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    
    const data = await response.json();
    return { data, status: response.status };
  },

  /**
   * DELETE request
   */
  async delete<T = unknown>(endpoint: string): Promise<{ data: T; status: number }> {
    const url = endpoint.startsWith('http') ? endpoint : `${BACKEND_URL}${endpoint}`;
    
    const response = await fetchWithAuth(url, { method: 'DELETE' });
    const data = await response.json();
    return { data, status: response.status };
  },
};

export default apiClient;
